import { css, styled } from 'canoe';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { ContentType } from '@candu-ai/content/types';
import { useIsVisible } from 'commons/hooks/useIsVisible';
import { BuiltInComponents } from 'craft/constants';

import { DocumentPreview } from '../DocumentPreview';
import { IFrameOverlay } from '../components/IFrameOverlay';

const DocumentContainer = styled.div<{ $width: string; $height: string }>`
  ${({ $width, $height }) => css<{ $width: string; $height: string }>`
    height: ${$height};
    width: ${$width};
    position: relative;
  `}
`;

const Transform = styled.div<{ $width: string; $height: string }>`
  ${({ $width, $height }) => css<{ $width: string; $height: string }>`
    left: 0;
    position: absolute;
    height: calc(${$height} * 5);
    width: calc(${$width} * 5);
    top: 0;
    transform: scale(0.2) translate(-200%, -200%);
    z-index: 0;

    &:after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      z-index: 1;
      pointer-events: none;
    }
  `}
`;

interface BaseProps {
  applicationId?: number;
  className?: string;
  width: string;
  height: string;
}

interface DocumentThumbnailProps extends BaseProps {
  documentId?: number;
}

interface DocumentVersionIdProps extends BaseProps {
  versionId?: string;
  contentType: ContentType;
}

interface TemplateThumbnailProps extends BaseProps {
  templateId?: string;
}

export const DocumentThumbnail = (
  props: DocumentThumbnailProps | TemplateThumbnailProps | DocumentVersionIdProps,
) => {
  const containerRef = useRef<any>();
  const { width, height, className } = props;

  const [renderPreview, setRenderPreview] = useState(false);
  const isVisible = useIsVisible(containerRef);

  const rootNodeType = useMemo(() => {
    if ('contentType' in props) {
      if (props.contentType === ContentType.Inline) {
        return BuiltInComponents.Document;
      }

      if (props.contentType === ContentType.Modal) {
        return BuiltInComponents.Modal;
      }

      if (props.contentType === ContentType.Tour) {
        return BuiltInComponents.Tour;
      }

      if (props.contentType === ContentType.HotspotGroup) {
        return BuiltInComponents.HotspotGroup;
      }

      return undefined;
    }

    return undefined;
  }, [props]);

  useEffect(() => {
    if (isVisible && !renderPreview) {
      setRenderPreview(true);
    }
  }, [isVisible]);

  return (
    <DocumentContainer $width={width} $height={height} className={className} ref={containerRef}>
      {renderPreview && (
        <Transform $width={width} $height={height}>
          <IFrameOverlay />
          {'versionId' in props && (
            // Only render directly with screenshot when we have a versionId available (node type needed)
            <DocumentPreview
              applicationId={props.applicationId}
              versionId={props.versionId}
              showEmptyDocumentFallback
              rootNodeType={rootNodeType}
            />
          )}
          {'documentId' in props && (
            <DocumentPreview
              applicationId={props.applicationId}
              documentId={props.documentId}
              showEmptyDocumentFallback
            />
          )}
          {'templateId' in props && (
            <DocumentPreview
              showEmptyDocumentFallback
              applicationId={props.applicationId}
              documentId={props.templateId}
              isTemplate
            />
          )}
        </Transform>
      )}
    </DocumentContainer>
  );
};
